import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const APPLE_AUTH_SCRIPT =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<{
          authorization: { identity_token?: string; identityToken?: string; id_token?: string; code?: string };
          user?: {
            email?: string;
            name?: { firstName: string; lastName: string };
          };
        }>;
      };
    };
  }
}

export interface AppleSignInResult {
  idToken: string;
  email?: string;
  name?: string;
}

interface AppleSSOModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with Apple identity token and optional email/name (Apple sends name only on first auth). */
  onSelectAccount: (result: AppleSignInResult) => void;
  /** Services ID from Apple Developer (same as Bonamassa APPLE_CLIENT_ID). Required for real Sign in with Apple. */
  appleClientId: string;
  /** Return URL — must match EXACTLY the Return URL in Apple Developer (e.g. with or without trailing slash). If empty, uses window.location.origin. */
  appleRedirectUri?: string;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export function AppleSSOModal({
  isOpen,
  onClose,
  onSelectAccount,
  appleClientId,
  appleRedirectUri,
}: AppleSSOModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setError(null);
    loadScript(APPLE_AUTH_SCRIPT)
      .then(() => setScriptLoaded(true))
      .catch(() => setError("Não foi possível carregar o Sign in with Apple. Tente novamente."));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !appleClientId || !scriptLoaded || !window.AppleID) return;

    // Redirect URI must match EXACTLY a Return URL in Apple Developer (Services ID → Configure).
    // When not set we use window.location.origin (no trailing slash). Add that exact URL in Apple Developer.
    const base =
      appleRedirectUri?.trim() ||
      (typeof window !== "undefined" ? window.location.origin : "");
    const redirectURI = base.replace(/\/$/, "");
    window.AppleID.auth.init({
      clientId: appleClientId,
      scope: "email name",
      redirectURI,
      state: "SignInWithApple",
      usePopup: true,
    });
  }, [isOpen, appleClientId, appleRedirectUri, scriptLoaded]);

  const handleAppleClick = async () => {
    if (!appleClientId || !window.AppleID) {
      setError("Sign in with Apple não configurado. Defina APPLE_CLIENT_ID (Services ID).");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const response = await window.AppleID.auth.signIn();
      // Apple JS SDK may return identity_token (snake_case) or identityToken (camelCase)
      const auth = response?.authorization as Record<string, unknown> | undefined;
      const idToken =
        (typeof auth?.identity_token === "string" && auth.identity_token) ||
        (typeof (auth as { identityToken?: string })?.identityToken === "string" &&
          (auth as { identityToken: string }).identityToken) ||
        (typeof auth?.id_token === "string" && auth.id_token) ||
        null;

      if (!idToken) {
        // Log structure (no token values) to trace Apple response shape
        const safe = response
          ? {
              hasResponse: true,
              hasAuthorization: !!response?.authorization,
              authKeys: response?.authorization
                ? Object.keys(response.authorization as object)
                : [],
              topLevelKeys: Object.keys(response as object),
            }
          : { hasResponse: false };
        console.warn("[Apple Sign-In] Invalid response: missing identity token.", safe);
        setError("Resposta inválida do Apple. Tente novamente.");
        return;
      }
      const user = response?.user;
      const email = user?.email?.trim();
      const nameParts = user?.name;
      const name =
        nameParts && (nameParts.firstName || nameParts.lastName)
          ? [nameParts.firstName, nameParts.lastName].filter(Boolean).join(" ").trim()
          : undefined;
      onSelectAccount({ idToken, email, name });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao entrar com Apple.";
      if (message.includes("popup") || message.includes("cancel") || message === "User cancelled") {
        setError(null);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-b from-gray-50 to-white px-6 py-5 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Entrar com Apple ID</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {!appleClientId ? (
            <p className="text-sm text-amber-600">
              Sign in with Apple não configurado. Defina APPLE_CLIENT_ID (Services ID) — mesmo valor do Bonamassa.
            </p>
          ) : error ? (
            <p className="text-sm text-red-600 mb-4">{error}</p>
          ) : null}

          <p className="text-sm text-gray-600 mb-4">
            Use seu Apple ID para entrar no akihub de forma segura.
          </p>

          <div ref={containerRef} className="flex flex-col items-center gap-4">
            <button
              type="button"
              onClick={handleAppleClick}
              disabled={!scriptLoaded || loading || !appleClientId}
              className="flex items-center justify-center gap-3 w-full max-w-[240px] h-12 px-6 bg-black text-white rounded-xl font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              {loading ? "Entrando…" : "Continuar com Apple"}
            </button>
          </div>
        </div>

        <div className="border-t border-gray-100 px-6 py-4 bg-gray-50">
          <p className="text-xs text-gray-600">
            Suas informações são protegidas pela Apple e nunca são compartilhadas sem sua permissão.
          </p>
        </div>
      </div>
    </div>
  );
}
